import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  Chip
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  fetchCourseVersionsData,
  addCourseVersion
} from "../../../../redux/actions/contentCoursesActions";
import { PropTypes } from "prop-types";
import AddPolicyVersionModal from "./AddPolicyVersionModal";
import Table from "../../../../components/Table/Table";
import MenuButton from "../../../../components/Buttons/MenuButton";
import ContentOwnerPolicyIcon from "../../../../components/Icons/ContentOwnerPolicyIcon";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { buildPolicyVersionDetailsRoute } from "../../../../routes/AdminRoutes";
import makeStyles from "@mui/styles/makeStyles";
import { contentHosts, addNewVersionOptions } from "../../../../constants";
import { fetchTableData } from "../../../../redux/utils/jsona";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import { some } from "lodash";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "no-wrap",
    "& > *": {
      margin: theme.spacing(0.5)
    }
  }
}));

const parseResponseErrors = errors => {
  if (
    some(
      errors,
      error => error.detail === "Number must be unique within manual"
    )
  ) {
    return { number: "managePolicies.error.duplicateNumber" };
  }
  return false;
};

const CourseVersionTitleCell = ({
  title,
  iconText,
  contentOwner,
  clientOwned
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box component="span" alignItems="center" className={classes.root}>
      <Typography>{title}</Typography>
      {iconText && iconText !== contentHosts.visionHosted && (
        <Chip label={t("manageCourses.legacyChip")} color="primary" disabled />
      )}
      {contentOwner && (
        <ContentOwnerPolicyIcon
          contentOwner={contentOwner}
          clientOwned={clientOwned}
        />
      )}
    </Box>
  );
};

const getCoursesTableColumns = t => [
  {
    title: "Title",
    field: "name",
    options: {
      sorting: true,
      filtering: true
    },
    filterPlaceholder: "titleAll",
    cellStyle: {
      minWidth: 240
    },
    render: rowData => (
      <CourseVersionTitleCell
        title={rowData.name}
        iconText={rowData.contentHost}
        contentOwner={rowData.contentOwner}
        clientOwned={rowData.clientOwned}
      />
    )
  },
  {
    title: "Language",
    field: "language",
    options: {
      sorting: true,
      filtering: true
    },
    filterPlaceholder: "languageAll",
    cellStyle: {
      minWidth: 100,
      textTransform: "capitalize"
    }
  },
  {
    title: "In Progress / Completed",
    field: "trainingHours",
    cellStyle: {
      minWidth: 190,
      textAlign: "center"
    },
    headerStyle: {
      textAlign: "center"
    },
    render: rowData => `${rowData.inProgress} / ${rowData.completed}`
  },
  {
    title: "Status",
    field: "status",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100,
      textTransform: "capitalize"
    }
  },
  {
    title: "Release",
    field: "release",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 120
    },
    render: rowData =>
      `${
        rowData.pending
          ? t("manageCourses.courseVersionsPendingRelease", {
              releaseVersion: rowData.release
            })
          : rowData.release
      }`
  }
];

const PolicyVersions = ({
  match: {
    params: { courseId },
    url
  },
  parentId,
  сustomizableContent
}) => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const [addVersionModalOpen, setAddVersionModalOpen] = useState(false);
  const [versionStatus, setVersionStatus] = useState("active");
  const [copyFromExisting, setCopyFromExisting] = useState(false);
  const id = courseId || parentId;

  useEffect(() => {
    updateTable();
  }, [versionStatus]);

  /**
   * Called when the query parameters are changed or a new course
   * version needs to be added. When the response is received,
   * if there was a new version added, we set the new version data
   * to null so we don't post it again and close the new version modal.
   */
  const fetchVersionsData = queryData => {
    return fetchTableData(
      dispatch(
        fetchCourseVersionsData(id, queryData, {
          status: versionStatus
        })
      )
    );
  };

  const updateTable = () =>
    tableRef.current && tableRef.current.onQueryChange();

  const handleAddNewVersion = async versionData => {
    return dispatch(
      addCourseVersion(
        id,
        versionData,
        newVersionData => {
          // Redirect user to newly created course
          dispatch(
            push(
              buildPolicyVersionDetailsRoute(id, Object.keys(newVersionData)[0])
            )
          );
        },
        error => {
          const errors = [...error.errors];
          if (!parseResponseErrors(errors)) handleResponseError(errors);
        }
      )
    );
  };

  const handleResponseError = errors => {
    const message = errors[0].detail
      ? errors[0].detail
      : t("managePolicies.addNewPolicyVersion.error");
    dispatch(enqueueErrorSnackbar({ message: message }));
  };

  const handleRowClick = (e, rowData) => {
    dispatch(push(buildPolicyVersionDetailsRoute(id, rowData.id)));
  };

  const columns = useMemo(() => {
    return getCoursesTableColumns(t);
  }, []);

  const versionsTable = useMemo(() => {
    return (
      <Table
        tableRef={tableRef}
        columns={columns}
        data={fetchVersionsData}
        itemType="record"
        hidePagination={true}
        onRowClick={handleRowClick}
      />
    );
  }, [versionStatus]);

  const handleAddNewPolicyVersion = addNewVersionOption => {
    if (addNewVersionOption === "copy") {
      setCopyFromExisting(true);
    } else {
      setCopyFromExisting(false);
    }
    setAddVersionModalOpen(true);
  };

  return (
    <Box display="flex" flexDirection="column" mt={4}>
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Box display="flex" flex={1} alignItems="center">
          <Box mr={3}>
            <Typography variant="h5">
              {t("manageCourses.courseVersionsFilterLabel")}
            </Typography>
          </Box>
          <TextField
            select
            value={versionStatus}
            onChange={({ target: { value } }) => setVersionStatus(value)}
            variant="filled"
            InputProps={{
              disableUnderline: true
            }}
            fullWidth
            id="status-filter"
            hiddenLabel
          >
            <MenuItem value="active">
              {t("manageCourses.courseVersionsFilterActiveOption")}
            </MenuItem>
            <MenuItem value="inactive">
              {t("manageCourses.courseVersionsFilterInactiveOption")}
            </MenuItem>
            <MenuItem value="all">
              {t("manageCourses.courseVersionsFilterAllOption")}
            </MenuItem>
          </TextField>
        </Box>
        <Box
          flex={1}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {сustomizableContent ? (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddVersionModalOpen(true)}
            >
              {t("manageCourses.courseVersionsAddNewBtn")}
            </Button>
          ) : (
            <MenuButton
              startIcon={<Add />}
              onClick={handleAddNewPolicyVersion}
              menuItems={Object.values(addNewVersionOptions).map(
                addNewVersionOption => ({
                  label: t(
                    "managePolicies.addNewPolicyVersion." +
                      addNewVersionOption.value
                  ),
                  value: addNewVersionOption.value
                })
              )}
            >
              {t("manageCourses.courseVersionsAddNewBtn")}
            </MenuButton>
          )}
        </Box>
      </Box>
      {versionsTable}
      <AddPolicyVersionModal
        open={addVersionModalOpen}
        onClose={() => setAddVersionModalOpen(false)}
        onSubmit={handleAddNewVersion}
        copyFromExisting={copyFromExisting}
        courseId={id}
      />
    </Box>
  );
};

PolicyVersions.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    })
  })
};

export default PolicyVersions;
